import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import { Loader2, Save, Image as ImageIcon, Upload } from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';
import ImageSuggestionModal from '../../../components/admin/ImageSuggestionModal';
import BunnyLibraryModal from '../../../components/admin/BunnyLibraryModal';
import TranslateButton from '../../../components/admin/TranslateButton';
import AutoTranslateIndicator from '../../../components/admin/AutoTranslateIndicator';
import { uploadImage } from '../../../services/imageService';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AboutForm = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    label_pt: 'SOBRE NÓS',
    label_en: 'ABOUT US',
    heading_pt: '',
    heading_en: '',
    paragraphs_pt: ['', '', ''],
    paragraphs_en: ['', '', ''],
    image_url: '',
    carousel_images: [],
    stats: [
      { number: '15+', label_pt: 'Anos de Experiência', label_en: 'Years of Experience' },
      { number: '50+', label_pt: 'Projetos Entregues', label_en: 'Projects Delivered' },
      { number: '3', label_pt: 'Continentes', label_en: 'Continents' },
      { number: '€127M+', label_pt: 'Capital Gerido', label_en: 'Capital Managed' }
    ]
  });

  useEffect(() => {
    loadAboutData();
  }, []);

  const loadAboutData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/homepage-ml/about?lang=pt`);
      if (response.data) {
        // Reconstruct PT/EN fields from the filtered response
        const ptData = response.data;
        const enResponse = await axios.get(`${API_URL}/api/homepage-ml/about?lang=en`);
        const enData = enResponse.data;
        
        setFormData({
          label_pt: ptData.label || 'SOBRE NÓS',
          label_en: enData.label || 'ABOUT US',
          heading_pt: ptData.heading || '',
          heading_en: enData.heading || '',
          paragraphs_pt: ptData.paragraphs || ['', '', ''],
          paragraphs_en: enData.paragraphs || ['', '', ''],
          image_url: ptData.image_url || '',
          carousel_images: ptData.carousel_images || [],
          stats: ptData.stats || []
        });
      }
    } catch (error) {
      console.error('Failed to load about data:', error);
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleParagraphChange = (lang, index, value) => {
    setFormData(prev => {
      const field = `paragraphs_${lang}`;
      const newParagraphs = [...prev[field]];
      newParagraphs[index] = value;
      return { ...prev, [field]: newParagraphs };
    });
  };

  const handleStatChange = (index, field, value) => {
    setFormData(prev => {
      const newStats = [...prev.stats];
      newStats[index] = { ...newStats[index], [field]: value };
      return { ...prev, stats: newStats };
    });
  };

  const handleImageSelect = (url) => {
    setFormData(prev => ({ ...prev, image_url: url }));
    toast({ title: "Imagem selecionada" });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ variant: "destructive", title: "Erro", description: "Selecione uma imagem válida" });
      return;
    }

    setUploading(true);
    try {
      const result = await uploadImage(file, 'images/about');
      if (result.success) {
        setFormData(prev => ({ ...prev, image_url: result.data.cdn_url }));
        toast({ title: "Upload completo" });
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Erro no upload" });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Build the update payload with both PT and EN fields
      const payload = {
        label_pt: formData.label_pt,
        label_en: formData.label_en,
        heading_pt: formData.heading_pt,
        heading_en: formData.heading_en,
        paragraphs_pt: formData.paragraphs_pt,
        paragraphs_en: formData.paragraphs_en,
        image_url: formData.image_url,
        stats: formData.stats
      };

      await axios.put(`${API_URL}/api/homepage-ml/about`, payload);
      
      toast({ title: "Sucesso", description: "Secção About guardada" });
    } catch (error) {
      toast({ variant: "destructive", title: "Erro", description: "Falha ao guardar" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <BackButton />

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Secção About</CardTitle>
            <CardDescription>Configure o conteúdo da secção About (PT/EN)</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Portuguese */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4 text-[#00B4D8]">🇵🇹 Português</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Label (PT)</label>
                    <input
                      type="text"
                      value={formData.label_pt}
                      onChange={(e) => handleInputChange('label_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Heading (PT)</label>
                    <input
                      type="text"
                      value={formData.heading_pt}
                      onChange={(e) => handleInputChange('heading_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  {formData.paragraphs_pt.map((para, idx) => (
                    <div key={idx}>
                      <label className="block text-sm font-medium mb-2">Parágrafo {idx + 1} (PT)</label>
                      <textarea
                        value={para}
                        onChange={(e) => handleParagraphChange('pt', idx, e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* English */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#00B4D8]">🇬🇧 English</h3>
                  <TranslateButton
                    sourceTexts={{
                      label: formData.label_pt,
                      heading: formData.heading_pt,
                      paragraph1: formData.paragraphs_pt[0],
                      paragraph2: formData.paragraphs_pt[1],
                      paragraph3: formData.paragraphs_pt[2]
                    }}
                    onTranslated={(translated) => {
                      setFormData(prev => ({
                        ...prev,
                        label_en: translated.label || '',
                        heading_en: translated.heading || '',
                        paragraphs_en: [
                          translated.paragraph1 || '',
                          translated.paragraph2 || '',
                          translated.paragraph3 || ''
                        ]
                      }));
                    }}
                  />
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Label (EN)</label>
                    <input
                      type="text"
                      value={formData.label_en}
                      onChange={(e) => handleInputChange('label_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Heading (EN)</label>
                    <input
                      type="text"
                      value={formData.heading_en}
                      onChange={(e) => handleInputChange('heading_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  {formData.paragraphs_en.map((para, idx) => (
                    <div key={idx}>
                      <label className="block text-sm font-medium mb-2">Paragraph {idx + 1} (EN)</label>
                      <textarea
                        value={para}
                        onChange={(e) => handleParagraphChange('en', idx, e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Carousel Images */}
              <div>
                <label className="block text-sm font-medium mb-3">About Carousel Images (zoom-in effect)</label>
                <p className="text-xs text-[#6C757D] mb-3">Add multiple images for the carousel. First image is the main one.</p>
                
                {/* Main Image */}
                <div className="mb-4">
                  <label className="text-xs font-medium text-[#6C757D]">Main Image (image_url)</label>
                  {formData.image_url && (
                    <div className="relative mb-2">
                      <img src={formData.image_url} alt="Main" className="w-full h-40 object-cover rounded-lg border" />
                      <Button type="button" variant="destructive" size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))} className="absolute top-2 right-2">
                        Remover
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input type="text" value={formData.image_url} onChange={(e) => handleInputChange('image_url', e.target.value)}
                      placeholder="https://..." className="flex-1 px-3 py-2 border rounded-md text-sm" />
                    <div>
                      <input type="file" id="about-upload" accept="image/*" onChange={handleFileUpload} className="hidden" />
                      <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('about-upload').click()}>
                        <Upload className="w-4 h-4 mr-1" /> {uploading ? '...' : 'Upload'}
                      </Button>
                    </div>
                    <Button type="button" variant="outline" size="sm" className="border-[#00B4D8] text-[#00B4D8]" onClick={() => setSuggestionsOpen(true)}>
                      <ImageIcon className="w-4 h-4 mr-1" /> Sugestões
                    </Button>
                  </div>
                </div>

                {/* Additional Carousel Images */}
                <label className="text-xs font-medium text-[#6C757D]">Additional Carousel Images</label>
                {(formData.carousel_images || []).map((img, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    {img && <img src={img} alt={`Slide ${idx+2}`} className="w-16 h-12 object-cover rounded border" />}
                    <input type="text" value={img} onChange={(e) => {
                      const imgs = [...(formData.carousel_images || [])];
                      imgs[idx] = e.target.value;
                      setFormData(prev => ({ ...prev, carousel_images: imgs }));
                    }} placeholder="Image URL" className="flex-1 px-3 py-2 border rounded-md text-sm" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => {
                      setFormData(prev => ({ ...prev, carousel_images: prev.carousel_images.filter((_, i) => i !== idx) }));
                    }}><span className="text-red-500">X</span></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="mt-2"
                  onClick={() => setFormData(prev => ({ ...prev, carousel_images: [...(prev.carousel_images || []), ''] }))}>
                  + Add Carousel Image
                </Button>
              </div>

              {/* Stats */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Estatísticas</h3>
                {formData.stats.map((stat, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-4 mb-3">
                    <input
                      type="text"
                      value={stat.number}
                      onChange={(e) => handleStatChange(idx, 'number', e.target.value)}
                      placeholder="Número"
                      className="px-3 py-2 border rounded"
                    />
                    <input
                      type="text"
                      value={stat.label_pt}
                      onChange={(e) => handleStatChange(idx, 'label_pt', e.target.value)}
                      placeholder="Label PT"
                      className="px-3 py-2 border rounded"
                    />
                    <input
                      type="text"
                      value={stat.label_en}
                      onChange={(e) => handleStatChange(idx, 'label_en', e.target.value)}
                      placeholder="Label EN"
                      className="px-3 py-2 border rounded"
                    />
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="space-y-4 pt-6 border-t">
                <AutoTranslateIndicator />
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => navigate('/admin/homepage')}>Cancelar</Button>
                  <Button type="submit" disabled={saving} className="bg-[#00B4D8]">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</> : <><Save className="w-4 h-4 mr-2" />Guardar</>}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <ImageSuggestionModal
        category="about"
        isOpen={suggestionsOpen}
        onClose={() => setSuggestionsOpen(false)}
        onSelect={handleImageSelect}
      />

      <BunnyLibraryModal
        folder="about"
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={handleImageSelect}
      />
    </div>
  );
};

export default AboutForm;